import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtNum } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Trash2, ArrowUpDown, ArrowUp, ArrowDown, FileDown, Calendar, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d;
}
function toIso(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function MesPointages() {
  const { user } = useAuth();
  const [view, setView] = useState('week'); // 'week' | 'month'
  const today = new Date();
  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [monthSel, setMonthSel] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [projets, setProjets] = useState(null);  // null = pas encore chargé
  const [allPointages, setAllPointages] = useState(null);
  const [extraCodes, setExtraCodes] = useState([]); // projets ajoutés manuellement cette semaine
  const [sort, setSort] = useState({ key: 'code', dir: 'asc' });
  const [sortMonth, toggleSortMonth] = useSort('date', 'asc');

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const daysIso = useMemo(() => days.map(toIso), [days]);

  const loadProjets = async () => {
    const { data } = await api.get('/projets');
    setProjets(data);
  };

  const loadAll = async () => {
    const { data } = await api.get('/pointages');
    setAllPointages(data);
  };

  useEffect(() => { loadProjets(); loadAll(); }, []);
  // Reset les codes ajoutés à la main quand on change de semaine
  useEffect(() => { setExtraCodes([]); }, [weekStart]);

  const isLoaded = projets !== null && allPointages !== null;
  const weekPointages = useMemo(() =>
    (allPointages || []).filter(p => p.date >= daysIso[0] && p.date <= daysIso[6]),
  [allPointages, daysIso]);

  const prevWeekCodes = useMemo(() => {
    const pStart = toIso(addDays(weekStart, -7));
    const pEnd = toIso(addDays(weekStart, -1));
    const codes = new Set();
    for (const p of (allPointages || [])) {
      if (p.date >= pStart && p.date <= pEnd) codes.add(p.projet_code);
    }
    return Array.from(codes);
  }, [allPointages, weekStart]);

  const projMap = useMemo(() => Object.fromEntries((projets || []).map(p => [p.code, p])), [projets]);

  // Codes affichés = semaine courante + semaine précédente (report) + codes ajoutés manuellement
  const codes = useMemo(() => {
    const s = new Set();
    weekPointages.forEach(p => s.add(p.projet_code));
    prevWeekCodes.forEach(c => s.add(c));
    extraCodes.forEach(c => s.add(c));
    return Array.from(s);
  }, [weekPointages, prevWeekCodes, extraCodes]);

  // Grille : code -> { iso -> pointage }
  const gridMap = useMemo(() => {
    const m = {};
    for (const c of codes) m[c] = {};
    for (const p of weekPointages) {
      if (!m[p.projet_code]) m[p.projet_code] = {};
      m[p.projet_code][p.date] = p;
    }
    return m;
  }, [codes, weekPointages]);

  const rowTotals = useMemo(() => {
    const r = {};
    for (const c of codes) r[c] = daysIso.reduce((s, d) => s + (gridMap[c][d]?.heures || 0), 0);
    return r;
  }, [codes, daysIso, gridMap]);

  const colTotals = useMemo(() =>
    daysIso.map(d => codes.reduce((s, c) => s + (gridMap[c][d]?.heures || 0), 0)),
  [codes, daysIso, gridMap]);
  const weekTotal = colTotals.reduce((s, x) => s + x, 0);

  const sortedCodes = useMemo(() => {
    const arr = [...codes];
    arr.sort((a, b) => {
      let va, vb;
      if (sort.key === 'code') { va = a; vb = b; }
      else if (sort.key === 'nom') { va = projMap[a]?.nom || ''; vb = projMap[b]?.nom || ''; }
      else if (sort.key === 'total') { va = rowTotals[a] || 0; vb = rowTotals[b] || 0; }
      else return 0;
      if (typeof va === 'number' && typeof vb === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
      return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [codes, sort, projMap, rowTotals]);

  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  const SortIcon = ({ k }) => {
    if (sort.key !== k) return <ArrowUpDown size={10} className="opacity-40" />;
    return sort.dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
  };

  const saveCell = async (projetCode, dateIso, newHeures) => {
    const h = Number(newHeures) || 0;
    const existing = gridMap[projetCode]?.[dateIso];
    try {
      if (h <= 0 && existing) {
        await api.delete(`/pointages/${existing.id}`);
      } else if (h > 0 && existing) {
        await api.put(`/pointages/${existing.id}`, {
          projet_code: projetCode, date: dateIso, heures: h,
          phase: existing.phase || 'projet', commentaire: existing.commentaire || null,
        });
      } else if (h > 0 && !existing) {
        await api.post('/pointages', {
          projet_code: projetCode, date: dateIso, heures: h, phase: 'projet', commentaire: null,
        });
      } else return;
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const addProjetRow = (code) => {
    const up = (code || '').toUpperCase().trim();
    if (!up) return;
    if (!projMap[up]) { toast.error('Code projet inconnu'); return; }
    if (codes.includes(up)) { toast.info('Projet déjà dans la liste'); return; }
    setExtraCodes(ec => [...ec, up]);
  };

  const removeRow = async (code) => {
    const hasHours = (rowTotals[code] || 0) > 0;
    if (hasHours && !window.confirm(`Retirer ${code} de la semaine ? Les ${fmtNum(rowTotals[code], 2)}h saisies seront supprimées.`)) return;
    // Supprime les pointages de la semaine pour ce projet
    const toDelete = weekPointages.filter(p => p.projet_code === code);
    for (const p of toDelete) {
      try { await api.delete(`/pointages/${p.id}`); } catch (err) { console.debug('[pointages] delete failed', p.id, err); }
    }
    setExtraCodes(ec => ec.filter(c => c !== code));
    await loadAll();
  };

  // Vue mois : liste de tous les pointages du mois sélectionné
  const monthPointages = useMemo(() => {
    const prefix = `${monthSel.year}-${String(monthSel.month + 1).padStart(2, '0')}`;
    return (allPointages || []).filter(p => p.date.startsWith(prefix));
  }, [allPointages, monthSel]);

  const monthTotalHours = useMemo(() => monthPointages.reduce((s, p) => s + p.heures, 0), [monthPointages]);

  const sortedMonthPointages = useMemo(() =>
    sortArr(monthPointages, sortMonth, (p, k) => {
      if (k === 'projet_nom') return projMap[p.projet_code]?.nom || '';
      return p[k];
    }),
  [monthPointages, sortMonth, projMap]);

  const prevMonth = () => setMonthSel(s => s.month === 0 ? { year: s.year - 1, month: 11 } : { ...s, month: s.month - 1 });
  const nextMonth = () => setMonthSel(s => s.month === 11 ? { year: s.year + 1, month: 0 } : { ...s, month: s.month + 1 });
  const thisMonth = () => setMonthSel({ year: today.getFullYear(), month: today.getMonth() });

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (err) { console.error('[pointages] download failed', err); toast.error('Erreur téléchargement'); }
  };

  const exportPdfMonth = () => {
    const { year, month } = monthSel;
    downloadFile(
      `/pointages/pdf/${user.id}?annee=${year}&mois=${month + 1}`,
      `Pointage_${user.prenom}_${user.nom}_${year}_${String(month + 1).padStart(2, '0')}.pdf`,
    );
  };

  const exportExcelMonth = () => {
    const { year, month } = monthSel;
    downloadFile(
      `/pointages/export-excel/${user.id}?annee=${year}&mois=${month + 1}`,
      `Pointage_${user.prenom}_${user.nom}_${year}_${String(month + 1).padStart(2, '0')}.xlsx`,
    );
  };

  const MONTHS_FULL = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  // Différenciation mois (si la semaine chevauche 2 mois)
  const months = days.map(d => d.getMonth());
  const firstMonth = months[0];
  const hasTwoMonths = new Set(months).size > 1;

  const weekLabel = `${days[0].toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → ${days[6].toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <Layout
      title="Mes pointages"
      subtitle={`${user?.prenom} ${user?.nom} — ${view === 'week' ? 'Vue semaine' : 'Vue mois'}`}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle Semaine / Mois */}
          <div className="flex border border-zinc-900" data-testid="view-toggle">
            <button onClick={() => setView('week')}
              className={`px-3 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 ${view === 'week' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
              data-testid="view-week">
              <LayoutGrid size={13} /> Semaine
            </button>
            <button onClick={() => setView('month')}
              className={`px-3 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-l border-zinc-900 ${view === 'month' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
              data-testid="view-month">
              <Calendar size={13} /> Mois
            </button>
          </div>
          {/* Navigation */}
          {view === 'week' ? (
            <>
              <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="prev-week"
                className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" title="Semaine précédente"><ChevronLeft size={16} /></button>
              <div className="px-4 py-2 border border-zinc-900 font-mono text-xs bg-white min-w-[240px] text-center" data-testid="week-label">{weekLabel}</div>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="next-week"
                className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" title="Semaine suivante"><ChevronRight size={16} /></button>
              <button onClick={() => setWeekStart(mondayOf(new Date()))}
                className="px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider"
                data-testid="this-week">Aujourd&apos;hui</button>
            </>
          ) : (
            <>
              <button onClick={prevMonth} data-testid="prev-month"
                className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" title="Mois précédent"><ChevronLeft size={16} /></button>
              <div className="px-4 py-2 border border-zinc-900 font-mono text-xs bg-white min-w-[180px] text-center" data-testid="month-label">{MONTHS_FULL[monthSel.month]} {monthSel.year}</div>
              <button onClick={nextMonth} data-testid="next-month"
                className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white" title="Mois suivant"><ChevronRight size={16} /></button>
              <button onClick={thisMonth}
                className="px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider"
                data-testid="this-month">Ce mois</button>
              <button onClick={exportExcelMonth} data-testid="export-excel"
                className="px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white text-xs uppercase tracking-wider inline-flex items-center gap-1.5" title="Exporter en Excel">
                <FileDown size={13} /> Excel
              </button>
              <button onClick={exportPdfMonth} data-testid="export-pdf"
                className="px-3 py-2 bg-[#002FA7] hover:bg-[#001B63] text-white text-xs uppercase tracking-wider inline-flex items-center gap-1.5" title="Exporter en PDF">
                <FileDown size={13} /> PDF
              </button>
            </>
          )}
        </div>
      }
    >
      {!isLoaded ? (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="mes-pointages-loading">
          <div className="inline-block w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mb-3" />
          <div className="text-sm text-zinc-600">Chargement de vos données…</div>
        </div>
      ) : view === 'month' ? (
        <MonthView
          monthSel={monthSel}
          pointages={sortedMonthPointages}
          projMap={projMap}
          total={monthTotalHours}
          sort={sortMonth}
          toggleSort={toggleSortMonth}
        />
      ) : (
      <>
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="mes-pointages-grid">
        <table className="w-full text-sm" style={{ minWidth: 980 }}>
          <thead className="border-b-2 border-zinc-900 bg-zinc-50">
            <tr>
              <th className="px-3 py-3 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-medium cursor-pointer select-none hover:bg-zinc-100"
                onClick={() => toggleSort('code')} data-testid="sort-code">
                <span className="inline-flex items-center gap-1">Code projet <SortIcon k="code" /></span>
              </th>
              <th className="px-3 py-3 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-medium cursor-pointer select-none hover:bg-zinc-100"
                onClick={() => toggleSort('nom')} data-testid="sort-nom">
                <span className="inline-flex items-center gap-1">Nom projet <SortIcon k="nom" /></span>
              </th>
              {days.map((d, i) => {
                const m = d.getMonth();
                const bg = hasTwoMonths && m !== firstMonth ? 'bg-amber-50' : '';
                const isWeekend = i >= 5;
                return (
                  <th key={i} className={`px-2 py-3 text-center text-[10px] uppercase tracking-[0.15em] font-medium w-[72px] ${bg} ${isWeekend ? 'text-zinc-400' : 'text-zinc-700'}`}>
                    <div>{DAY_LABELS[i]}</div>
                    <div className="font-mono text-[10px] font-normal mt-0.5">{String(d.getDate()).padStart(2, '0')}/{String(d.getMonth() + 1).padStart(2, '0')}</div>
                  </th>
                );
              })}
              <th className="px-3 py-3 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-700 font-medium cursor-pointer select-none hover:bg-zinc-100 bg-zinc-100"
                onClick={() => toggleSort('total')} data-testid="sort-total">
                <span className="inline-flex items-center gap-1 flex-row-reverse">Total hebdo <SortIcon k="total" /></span>
              </th>
              <th className="w-[40px]"></th>
            </tr>
          </thead>
          <tbody>
            {sortedCodes.map((code) => {
              const pr = projMap[code];
              return (
                <tr key={`${toIso(weekStart)}-${code}`} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-3 py-2 font-mono text-xs font-medium text-[#002FA7]">{code}</td>
                  <td className="px-3 py-2 text-xs">{pr?.nom || <span className="text-rose-600">code inconnu</span>}</td>
                  {daysIso.map((d, i) => {
                    const m = days[i].getMonth();
                    const bg = hasTwoMonths && m !== firstMonth ? 'bg-amber-50/60' : '';
                    const cell = gridMap[code]?.[d];
                    const isWeekend = i >= 5;
                    return (
                      <td key={d} className={`px-1 py-1 text-center ${bg} ${isWeekend ? 'bg-zinc-50/40' : ''}`}>
                        <input type="number" min="0" step="0.25"
                          key={`${toIso(weekStart)}-${code}-${d}-${cell?.id || 'new'}-${cell?.heures ?? ''}`}
                          defaultValue={cell?.heures ?? ''}
                          onBlur={(e) => {
                            const v = e.target.value;
                            const prev = cell?.heures ?? 0;
                            if (Number(v || 0) !== prev) saveCell(code, d, v);
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className="w-14 h-8 px-1.5 py-0.5 border border-zinc-200 font-mono text-right text-xs rounded-none focus:outline-none focus:border-[#002FA7] focus:bg-white bg-transparent [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          data-testid={`cell-${code}-${d}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 font-mono text-right text-xs font-semibold bg-zinc-50">
                    {(rowTotals[code] || 0) > 0 ? `${fmtNum(rowTotals[code], 2)} h` : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => removeRow(code)} className="p-1 hover:bg-rose-100 text-rose-600" title="Retirer ce projet" data-testid={`delete-row-${code}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {/* Ligne d'ajout */}
            <tr className="border-b-2 border-zinc-300 bg-blue-50/30">
              <td colSpan={2} className="px-3 py-2">
                <Select value="" onValueChange={addProjetRow}>
                  <SelectTrigger className="rounded-none h-9 text-xs border-dashed border-zinc-400" data-testid="add-row-select">
                    <SelectValue placeholder="+ Ajouter un projet à la semaine…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-80">
                    {(projets || []).filter(p => !codes.includes(p.code)).map(p => (
                      <SelectItem key={p.code} value={p.code}>
                        <span className="font-mono text-[#002FA7]">{p.code}</span> — {p.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td colSpan={9}></td>
            </tr>
            {sortedCodes.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-zinc-400 text-sm">
                Aucun projet cette semaine. Ajoutez-en un ci-dessus pour commencer à saisir vos heures.
              </td></tr>
            )}
          </tbody>
          <tfoot className="bg-zinc-100 border-t-2 border-zinc-900 font-semibold">
            <tr>
              <td colSpan={2} className="px-3 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-900">Total journalier</td>
              {colTotals.map((t, i) => (
                <td key={i} className="px-2 py-3 font-mono text-center text-xs" data-testid={`total-day-${i}`}>
                  {t > 0 ? `${fmtNum(t, 1)} h` : <span className="text-zinc-300">—</span>}
                </td>
              ))}
              <td className="px-3 py-3 font-mono text-right text-sm text-[#002FA7]" data-testid="total-week">{fmtNum(weekTotal, 1)} h</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {hasTwoMonths && (
        <div className="mt-3 px-4 py-2 bg-white border border-zinc-200 text-[11px] text-zinc-600 flex items-center gap-4" data-testid="months-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-white border border-zinc-300"></span>{MONTHS_FR[firstMonth]}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 inline-block bg-amber-50 border border-amber-200"></span>{MONTHS_FR[months[6]]}</span>
          <span className="text-zinc-400 ml-auto">Cette semaine couvre deux mois</span>
        </div>
      )}

      <div className="mt-4 text-[11px] text-zinc-500 italic">
        Astuce : saisissez vos heures au quart d&apos;heure (0,25 = 15 min). Les projets de la semaine précédente sont reportés automatiquement, à vous de remplir les heures. Supprimez une ligne avec l&apos;icône corbeille à droite.
      </div>
      </>
      )}
    </Layout>
  );
}

function MonthView({ monthSel, pointages, projMap, total, sort, toggleSort }) {
  const MONTHS_FULL = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  // Synthèse par projet
  const byProjet = useMemoByProjet(pointages, projMap);
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-4" data-testid="month-stat-total">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Total heures</div>
          <div className="font-mono font-medium text-2xl">{fmtNum(total, 1)} h</div>
          <div className="text-[10px] text-zinc-500 mt-1">{MONTHS_FULL[monthSel.month]} {monthSel.year}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Projets distincts</div>
          <div className="font-mono font-medium text-2xl">{byProjet.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Nb de saisies</div>
          <div className="font-mono font-medium text-2xl">{pointages.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Synthèse */}
        <div className="lg:col-span-2 bg-white border border-zinc-200" data-testid="month-by-projet">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Synthèse par projet</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="px-4 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Projet</th>
                <th className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Heures</th>
              </tr>
            </thead>
            <tbody>
              {byProjet.map((b) => (
                <tr key={b.code} className="border-b border-zinc-100">
                  <td className="px-4 py-2">
                    <div className="font-medium text-xs">{b.nom}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{b.code}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-right text-xs">{fmtNum(b.heures, 2)}</td>
                </tr>
              ))}
              {byProjet.length === 0 && <tr><td colSpan={2} className="px-4 py-8 text-center text-zinc-500 text-xs">Aucun pointage ce mois.</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Détail */}
        <div className="lg:col-span-3 bg-white border border-zinc-200" data-testid="month-detail">
          <div className="border-b border-zinc-900 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-medium">Détail ({pointages.length})</div>
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-zinc-200 z-10">
                <tr className="text-left">
                  <SortHeader sort={sort} onToggle={toggleSort} k="date" label="Date" />
                  <SortHeader sort={sort} onToggle={toggleSort} k="projet_nom" label="Projet" />
                  <SortHeader sort={sort} onToggle={toggleSort} k="heures" label="Heures" align="right" />
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Commentaire</th>
                </tr>
              </thead>
              <tbody>
                {pointages.map((p) => {
                  const pr = projMap[p.projet_code];
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{p.date}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-xs">{pr?.nom || '—'}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{p.projet_code}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-right text-xs">{fmtNum(p.heures, 2)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-600">{p.commentaire || '—'}</td>
                    </tr>
                  );
                })}
                {pointages.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500 text-sm">Aucun pointage ce mois.</td></tr>}
              </tbody>
              <tfoot className="bg-zinc-100 border-t-2 border-zinc-900 font-semibold">
                <tr>
                  <td colSpan={2} className="px-3 py-2 text-[11px] uppercase tracking-[0.15em]">Total</td>
                  <td className="px-3 py-2 font-mono text-right text-sm text-[#002FA7]">{fmtNum(total, 1)} h</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-zinc-500 italic">
        Astuce : cliquez sur les boutons <strong>Excel</strong> ou <strong>PDF</strong> en haut à droite pour extraire la feuille mensuelle (utile pour relecture ou envoi à l&apos;admin).
      </div>
    </>
  );
}

function useMemoByProjet(pointages, projMap) {
  return React.useMemo(() => {
    const map = {};
    for (const p of pointages) {
      if (!map[p.projet_code]) map[p.projet_code] = { code: p.projet_code, heures: 0 };
      map[p.projet_code].heures += p.heures;
    }
    return Object.values(map)
      .map(r => ({ ...r, nom: projMap[r.code]?.nom || '—' }))
      .sort((a, b) => b.heures - a.heures);
  }, [pointages, projMap]);
}
